import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiX, FiCheck } from 'react-icons/fi';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Check if user has already accepted cookies
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      // Show banner after a small delay
      setTimeout(() => {
        setShowBanner(true);
      }, 1000);
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      necessary: true,
      functional: true,
      analytics: true,
      timestamp: new Date().toISOString()
    }));
    setShowBanner(false);
  };

  const handleAcceptNecessary = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      necessary: true,
      functional: false,
      analytics: false,
      timestamp: new Date().toISOString()
    }));
    setShowBanner(false);
  };

  const handleReject = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      necessary: true,
      functional: false,
      analytics: false,
      timestamp: new Date().toISOString()
    }));
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-end justify-center pointer-events-none">
      {/* Overlay */}
      {showDetails && (
        <div
          className="absolute inset-0 bg-black/20 pointer-events-auto"
          onClick={() => setShowDetails(false)}
        />
      )}

      {/* Cookie Banner */}
      <div className="relative w-full max-w-7xl mx-4 mb-4 pointer-events-auto">
        <div className="bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden">
          {/* Close button */}
          <button
            onClick={handleReject}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Cerrar"
          >
            <FiX className="w-6 h-6" />
          </button>

          <div className="p-6">
            {/* Main Content */}
            {!showDetails ? (
              <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                <div className="flex-1 pr-8">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    🍪 Usamos cookies
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Utilizamos cookies propias y de terceros para mejorar su experiencia de navegación,
                    analizar el uso de nuestros servicios y personalizar el contenido. Puede aceptar todas
                    las cookies o elegir sus preferencias.
                    {' '}
                    <Link to="/privacy" className="text-blue-600 hover:text-blue-700 underline">
                      Política de Privacidad
                    </Link>
                  </p>
                </div>

                {/* Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                  <button
                    onClick={() => setShowDetails(true)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm font-medium whitespace-nowrap"
                  >
                    Configurar
                  </button>
                  <button
                    onClick={handleAcceptNecessary}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm font-medium whitespace-nowrap"
                  >
                    Solo Necesarias
                  </button>
                  <button
                    onClick={handleAcceptAll}
                    className="px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-md hover:shadow-lg text-sm font-medium whitespace-nowrap"
                  >
                    Aceptar Todas
                  </button>
                </div>
              </div>
            ) : (
              /* Detailed Settings */
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    Configuración de Cookies
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Elige qué tipos de cookies deseas permitir. Las cookies necesarias son obligatorias
                    para el funcionamiento básico del sitio.
                  </p>
                </div>

                {/* Cookie Types */}
                <div className="space-y-4">
                  {/* Necessary Cookies */}
                  <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                    <div className="mt-0.5">
                      <div className="w-5 h-5 bg-blue-600 rounded flex items-center justify-center">
                        <FiCheck className="w-3 h-3 text-white" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-gray-900">Cookies Necesarias</h4>
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                          Siempre activas
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">
                        Estas cookies son esenciales para que el sitio web funcione correctamente.
                        Incluyen funciones como inicio de sesión, seguridad y preferencias básicas.
                      </p>
                    </div>
                  </div>

                  {/* Functional Cookies */}
                  <div className="flex items-start gap-3 p-4 border border-gray-200 rounded-lg">
                    <input
                      type="checkbox"
                      id="functional"
                      defaultChecked
                      className="mt-1 w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="functional" className="flex-1 cursor-pointer">
                      <h4 className="font-semibold text-gray-900 mb-1">Cookies Funcionales</h4>
                      <p className="text-sm text-gray-600">
                        Estas cookies permiten funciones mejoradas y personalización, como recordar
                        sus preferencias de idioma o región.
                      </p>
                    </label>
                  </div>

                  {/* Analytics Cookies */}
                  <div className="flex items-start gap-3 p-4 border border-gray-200 rounded-lg">
                    <input
                      type="checkbox"
                      id="analytics"
                      defaultChecked
                      className="mt-1 w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="analytics" className="flex-1 cursor-pointer">
                      <h4 className="font-semibold text-gray-900 mb-1">Cookies de Análisis</h4>
                      <p className="text-sm text-gray-600">
                        Estas cookies nos ayudan a entender cómo los visitantes interactúan con el sitio,
                        recopilando información de forma anónima sobre las páginas visitadas y errores.
                      </p>
                    </label>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => setShowDetails(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm font-medium"
                  >
                    Volver
                  </button>
                  <div className="flex-1" />
                  <button
                    onClick={handleAcceptNecessary}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm font-medium"
                  >
                    Solo Necesarias
                  </button>
                  <button
                    onClick={() => {
                      const functional = document.getElementById('functional')?.checked;
                      const analytics = document.getElementById('analytics')?.checked;

                      localStorage.setItem('cookieConsent', JSON.stringify({
                        necessary: true,
                        functional: functional || false,
                        analytics: analytics || false,
                        timestamp: new Date().toISOString()
                      }));
                      setShowBanner(false);
                    }}
                    className="px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-md hover:shadow-lg text-sm font-medium"
                  >
                    Guardar Preferencias
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
